import React, { useState } from 'react';
import { FiX as X, FiStar as Sparkles, FiBarChart2 } from 'react-icons/fi';

const DATA_SOURCES: Record<string, string> = {
  projects: 'Projects',
  costs: 'Costs & Budget',
  timesheets: 'Timesheets',
  support: 'Support Tickets',
  llm: 'LLM Usage',
  dataops: 'Data Pipelines',
  agents: 'Agent Runs',
  compliance: 'Compliance & Guardrails',
  environments: 'Environments',
  tagging: 'Resource Tagging'
};

const AGGREGATIONS: Record<string, string> = {
  count: 'Count',
  sum: 'Sum',
  avg: 'Average',
  max: 'Maximum',
  min: 'Minimum',
  percentage: 'Percentage'
};

const CHART_TYPES: Record<string, string> = {
  number: 'Number',
  bar: 'Bar Chart',
  line: 'Line Chart',
  pie: 'Pie Chart',
  area: 'Area Chart'
};

interface FilterDefinition {
  id: string;
  name: string;
  values: string[];
}

const FILTERS: Record<string, FilterDefinition[]> = {
  projects: [
    { id: 'status', name: 'Status', values: ['active', 'completed', 'on-hold', 'planning'] },
    { id: 'client', name: 'Client', values: [] },
    { id: 'budget_range', name: 'Budget Range', values: ['<$50k', '$50k-$100k', '$100k-$500k', '>$500k'] }
  ],
  costs: [
    { id: 'service', name: 'AWS Service', values: ['EC2', 'S3', 'RDS', 'Lambda', 'Other'] },
    { id: 'time_range', name: 'Time Range', values: ['Last 7 days', 'Last 30 days', 'Last 90 days', 'This year'] }
  ],
  timesheets: [
    { id: 'billable', name: 'Billable', values: ['Yes', 'No'] },
    { id: 'project', name: 'Project', values: [] },
    { id: 'person', name: 'Person', values: [] }
  ],
  support: [
    { id: 'priority', name: 'Priority', values: ['critical', 'high', 'medium', 'low'] },
    { id: 'status', name: 'Status', values: ['open', 'pending', 'resolved', 'closed'] }
  ],
  llm: [
    { id: 'model', name: 'Model', values: ['gpt-4', 'gpt-3.5-turbo', 'claude-3', 'claude-2'] },
    { id: 'status', name: 'Status', values: ['success', 'error'] }
  ],
  dataops: [
    { id: 'status', name: 'Run Status', values: ['success', 'failed', 'running'] },
    { id: 'type', name: 'Pipeline Type', values: ['ETL', 'ML', 'Analytics'] }
  ],
  agents: [
    { id: 'status', name: 'Status', values: ['completed', 'failed', 'running'] },
    { id: 'agent', name: 'Agent', values: [] }
  ],
  compliance: [
    { id: 'severity', name: 'Severity', values: ['critical', 'high', 'medium', 'low'] },
    { id: 'category', name: 'Category', values: ['security', 'cost', 'governance', 'reliability'] }
  ],
  environments: [
    { id: 'health', name: 'Health Status', values: ['healthy', 'degraded', 'down'] },
    { id: 'environment', name: 'Environment', values: ['production', 'staging', 'development'] }
  ],
  tagging: [
    { id: 'compliance', name: 'Compliance', values: ['compliant', 'non-compliant'] },
    { id: 'tag_key', name: 'Tag Key', values: ['Project', 'Environment', 'CostCenter', 'Owner'] }
  ]
};

interface FilterEntry {
  field: string;
  value: string;
}

interface MetricConfig {
  name: string;
  dataSource: string;
  field: string;
  aggregation: string;
  filters: FilterEntry[];
  chartType: string;
  color: string;
}

interface PreviewData {
  value: number;
  label: string;
  format: string;
  color: string;
}

interface ColorOption {
  name: string;
  value: string;
  class: string;
}

interface Props {
  onSave: (config: MetricConfig) => void;
  onClose: () => void;
}

const CustomMetricsBuilder: React.FC<Props> = ({ onSave, onClose }) => {
  const [config, setConfig] = useState<MetricConfig>({
    name: '',
    dataSource: '',
    field: '',
    aggregation: 'count',
    filters: [],
    chartType: 'number',
    color: 'blue'
  });

  const [previewData, setPreviewData] = useState<PreviewData | null>(null);

  const availableFilters = FILTERS[config.dataSource] || [];

  const handleAddFilter = (): void => {
    setConfig({
      ...config,
      filters: [...config.filters, { field: '', value: '' }]
    });
  };

  const handleRemoveFilter = (index: number): void => {
    setConfig({
      ...config,
      filters: config.filters.filter((_, i) => i !== index)
    });
  };

  const handleFilterChange = (index: number, key: 'field' | 'value', value: string): void => {
    const newFilters = [...config.filters];
    newFilters[index] = { ...newFilters[index], [key]: value };
    setConfig({ ...config, filters: newFilters });
  };

  const handlePreview = (): void => {
    const mockValue = Math.floor(Math.random() * 1000);
    setPreviewData({
      value: mockValue,
      label: config.name || 'Custom Metric',
      format: config.aggregation === 'percentage' ? 'percentage' : 'number',
      color: config.color
    });
  };

  const handleSave = (): void => {
    if (!config.name || !config.dataSource) {
      alert('Please provide a name and select a data source');
      return;
    }
    onSave(config);
  };

  const COLORS: ColorOption[] = [
    { name: 'Blue', value: 'blue', class: 'bg-blue-500' },
    { name: 'Cyan', value: 'cyan', class: 'bg-cyan-400' },
    { name: 'Dark Cyan', value: 'darkcyan', class: 'bg-cyan-500' },
    { name: 'Light Blue', value: 'lightblue', class: 'bg-blue-400' },
    { name: 'Aqua', value: 'aqua', class: 'bg-cyan-400' },
    { name: 'Dark Blue', value: 'darkblue', class: 'bg-blue-600' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col transition-colors">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white transition-colors">
                Custom Metrics Builder
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 transition-colors">
                Create your own metrics with automated graphs
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Configuration */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 transition-colors">
                  Configuration
                </h3>

                <div className="space-y-2">
                  <label htmlFor="metric-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                    Metric Name
                  </label>
                  <input id="metric-name"
                    type="text"
                    value={config.name}
                    onChange={(e) => setConfig({ ...config, name: e.target.value })}
                    placeholder="e.g., Active Projects by Client"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                  />
                </div>

                <div className="space-y-2 mt-4">
                  <label htmlFor="data-source" className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                    Data Source
                  </label>
                  <select id="data-source"
                    value={config.dataSource}
                    onChange={(e) => setConfig({ ...config, dataSource: e.target.value, filters: [] })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                  >
                    <option value="">Select data source...</option>
                    {Object.entries(DATA_SOURCES).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2 mt-4">
                  <label htmlFor="aggregation" className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                    Aggregation
                  </label>
                  <select id="aggregation"
                    value={config.aggregation}
                    onChange={(e) => setConfig({ ...config, aggregation: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                  >
                    {Object.entries(AGGREGATIONS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2 mt-4">
                  <label htmlFor="visualization" className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                    Visualization
                  </label>
                  <select id="visualization"
                    value={config.chartType}
                    onChange={(e) => setConfig({ ...config, chartType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                  >
                    {Object.entries(CHART_TYPES).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2 mt-4">
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                    Color
                  </span>
                  <div className="flex gap-2">
                    {COLORS.map((colorOption) => (
                      <button
                        key={colorOption.value}
                        onClick={() => setConfig({ ...config, color: colorOption.value })}
                        className={`w-10 h-10 rounded-lg ${colorOption.class} ${
                          config.color === colorOption.value
                            ? 'ring-2 ring-offset-2 ring-gray-900 dark:ring-white'
                            : ''
                        }`}
                        title={colorOption.name}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Filters & Preview */}
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors">
                    Filters
                  </h3>
                  <button
                    onClick={handleAddFilter}
                    disabled={!config.dataSource}
                    className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    + Add Filter
                  </button>
                </div>

                {config.filters.length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg transition-colors">
                    No filters applied. Add filters to refine your metric.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {config.filters.map((filter, index) => (
                      <div key={`filter-${filter.field || 'empty'}-${index}`} className="flex gap-2 items-start">
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <select
                            value={filter.field}
                            onChange={(e) => handleFilterChange(index, 'field', e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                          >
                            <option value="">Select field...</option>
                            {availableFilters.map((f) => (
                              <option key={f.id} value={f.id}>{f.name}</option>
                            ))}
                          </select>
                          <select
                            value={filter.value}
                            onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                            disabled={!filter.field}
                          >
                            <option value="">Select value...</option>
                            {filter.field && availableFilters
                              .find(f => f.id === filter.field)
                              ?.values.map((val) => (
                                <option key={val} value={val}>{val}</option>
                              ))}
                          </select>
                        </div>
                        <button
                          onClick={() => handleRemoveFilter(index)}
                          className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/50 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Preview */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors">
                    Preview
                  </h3>
                  <button
                    onClick={handlePreview}
                    disabled={!config.name || !config.dataSource}
                    className="px-3 py-1 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Generate Preview
                  </button>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 min-h-[200px] flex items-center justify-center transition-colors">
                  {previewData ? (
                    <div className="text-center">
                      <div className={`text-4xl font-bold text-${previewData.color}-600 dark:text-${previewData.color}-400 mb-2`}>
                        {previewData.format === 'percentage'
                          ? `${previewData.value}%`
                          : previewData.value.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {previewData.label}
                      </div>
                      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                        Mock data - actual values will vary
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 dark:text-gray-400">
                      <FiBarChart2 className="w-10 h-10 mb-2 mx-auto" />
                      <div className="text-sm">
                        Click "Generate Preview" to see your metric
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!config.name || !config.dataSource}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Create Metric
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomMetricsBuilder;
